import { OverlayRef } from '@angular/cdk/overlay';
import { DomPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  input,
  signal,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'abb-bottom-sheet',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      id="bottom-sheet"
      class="bg-abb-background flex flex-row top-shadow transition-all h-12 border-b-4 border-t-4 border-b-abb-primary border-t-transparent rounded-t-lg"
      [ngClass]="
        open() ? 'open translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      "
      [ngStyle]="{ width: width() }"
    >
      <ng-content></ng-content>
    </div>
    @if(hasBackdrop()) {
    <div
      #backdrop
      class="fixed z-side-panel-backdrop inset-0 bg-abb-black/40 transition-opacity"
      (click)="onBackdropClick()"
      [ngStyle]="{ opacity: backdropOpacity() }"
    ></div>
    }
  `,
  styles: `
    .top-shadow {
      box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06);
    }
  `,
})
export class AbbBottomSheetComponent implements AfterViewInit, OnDestroy {
  closable = input(true);
  backdropClickClosable = input(false);
  hasBackdrop = input(false);
  onBeforeClose = input<(() => Promise<boolean>) | undefined>();
  onClose = input<() => void>();
  open = signal(false);
  sidenavOpened = signal(true);
  width = signal('calc(100vw - 20rem)');
  subscriptions: Subscription[] = [];
  backdropOpacity = signal(0);
  private portal: DomPortal<HTMLDivElement> | undefined;
  @ViewChild('backdrop') backdrop: ElementRef<HTMLDivElement> | undefined;
  sidenav: HTMLElement | null;
  updateBottomSheetWidth = () => {
    this.zone.run(() => {
      this.width.set(
        this.sidenav?.getAttribute('open') === 'true'
          ? 'calc(100vw - 20rem)'
          : 'calc(100vw - 10rem)'
      );
      const root = document.documentElement;
      root.style.setProperty(
        '--internalPageWidth',
        this.sidenav?.getAttribute('open') === 'true'
          ? 'calc(100vw - 15.933rem)'
          : 'calc(100vw - 5.7rem)'
      );
    });
  };
  resizeObserver = new ResizeObserver(this.updateBottomSheetWidth);
  constructor(
    protected overlayRef: OverlayRef,
    router: Router,
    private zone: NgZone
  ) {
    this.sidenav = document.getElementById('sidenav');
    if (this.sidenav) {
      this.resizeObserver.observe(this.sidenav);
    }
    this.changeSidenavContentHeight(true);
    // close overlay if route changes (e.g. user clicked on another menu entry)
    this.subscriptions.push(
      router.events.subscribe((e) => {
        if (e instanceof NavigationStart) {
          this.overlayRef.detach();
        }
      })
    );
  }

  onBackdropClick() {
    if (this.backdropClickClosable() && this.closable()) {
      this.handleClose();
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.portal?.detach();
    this.changeSidenavContentHeight();
    this.resizeObserver.disconnect();
  }

  private changeSidenavContentHeight(adding: boolean = false): void {
    const sidenavContent = document.getElementById('sidenav-content');
    sidenavContent?.style.setProperty(
      'height',
      adding ? 'calc(100% - 4rem)' : ''
    );
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => this.open.set(true));
    if (this.backdrop && this.hasBackdrop()) {
      this.portal = new DomPortal(this.backdrop.nativeElement);
      this.portal.attach(
        new DomPortalOutlet(
          document.body,
          undefined,
          undefined,
          undefined,
          document
        )
      );
      requestAnimationFrame(() => {
        this.backdropOpacity.set(1);
      });
    }
  }
  async handleClose() {
    const onBeforeClose = this.onBeforeClose();
    if (onBeforeClose) {
      const canContinue = await onBeforeClose();
      if (!canContinue) {
        return;
      }
    }
    this.onClose()?.();
    this.dispose();
  }

  dispose() {
    this.open.set(false);
    setTimeout(() => {
      this.overlayRef.dispose();
    }, 150);
  }
}
